// Reads the release notes the badge shows.
//
// The database is the copy the running app reads, so a note corrected after
// the fact does not need a redeploy. src/lib/releases.json stays the authored
// source and is compiled in as the fallback, which is what makes a missing or
// behind migration harmless: query error, no rows, or a table that does not
// exist yet all land on the compiled list rather than an empty badge.

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { RELEASES, type Release } from '@/lib/changelog'
import { pickReleases, releasesFromRows, type AppReleaseRow } from '@/lib/app-releases'

export function useReleases(): Release[] {
  const { data } = useQuery({
    queryKey: ['app-releases'],
    // Release notes change on a deploy, not during a shift.
    staleTime: 30 * 60 * 1000,
    retry: false,
    queryFn: async (): Promise<Release[] | null> => {
      // src/integrations/supabase/types.ts is GENERATED and does not know
      // app_releases yet, so the typed client rejects the table name. Cast at
      // this one call rather than hand-editing a generated file, which would
      // drift the moment types are regenerated. The shape is asserted below
      // via AppReleaseRow, and releasesFromRows drops anything malformed.
      const { data: rows, error } = await (supabase as unknown as {
        from: (t: string) => {
          select: (q: string) => Promise<{ data: unknown[] | null; error: unknown }>
        }
      })
        .from('app_releases')
        .select('version, released_on, headline, app_release_changes(position, kind, text, roadmap_refs)')

      // Never surface this. A badge that cannot reach the table shows the
      // compiled notes instead, which is a correct answer, not a degraded one.
      if (error) return null
      return releasesFromRows((rows ?? []) as unknown as AppReleaseRow[])
    },
  })

  return pickReleases(data, RELEASES)
}
